// ============================================
// AnimatedMarker — Yango/InDrive-Grade Smooth Marker
// 60fps animation, speed-adaptive duration, heading prediction
// ============================================

import { useEffect, useRef, useCallback } from 'react';

/**
 * AnimatedMarker — Production-grade smooth Google Maps marker
 * 
 * Features:
 *  - Animates from CURRENT animated position to new target (no jump-back)
 *  - Speed-adaptive duration: fast movement → shorter animation, slow → longer
 *  - Dead-zone: ignores position changes < ~0.8m to prevent jitter
 *  - Cubic ease-in-out for natural car motion feel
 *  - 360° heading interpolation with shortest-path wrap
 *  - Supports both SVG data URI icons and Symbol icons
 */
const AnimatedMarker = ({ map, position, heading, icon, iconGenerator, label, zIndex, onClick, duration = 1200, speed }) => {
    const markerRef = useRef(null);
    const animRef = useRef(null);
    const currentPosRef = useRef(null);
    const targetPosRef = useRef(null);
    const currentHeadingRef = useRef(heading || 0);

    // Create marker once
    useEffect(() => {
        if (!map || !window.google) return;

        const marker = new window.google.maps.Marker({
            map,
            position: position ? new window.google.maps.LatLng(position.lat, position.lng) : undefined,
            icon: iconGenerator ? iconGenerator(heading || 0) : icon,
            label,
            zIndex: zIndex || 999,
            optimized: false,
        });

        markerRef.current = marker;
        currentPosRef.current = position ? { ...position } : null;
        targetPosRef.current = position ? { ...position } : null;
        currentHeadingRef.current = heading || 0;

        if (onClick) marker.addListener('click', onClick);

        return () => {
            if (animRef.current) cancelAnimationFrame(animRef.current);
            marker.setMap(null);
            markerRef.current = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [map]);

    // Update icon dynamically (for state changes)
    useEffect(() => {
        if (markerRef.current && icon) markerRef.current.setIcon(icon);
    }, [icon]);

    // Update label dynamically
    useEffect(() => {
        if (markerRef.current && label) markerRef.current.setLabel(label);
    }, [label]);

    // ── Compute speed-adaptive animation duration ──
    const getAdaptiveDuration = useCallback((spd) => {
        if (!spd && spd !== 0) return duration;
        const speedKmh = typeof spd === 'number' ? spd : 0;
        
        if (speedKmh > 60) return 700;    // Highway: fast, snappy
        if (speedKmh > 30) return 900;    // City driving: balanced
        if (speedKmh > 10) return 1100;   // Residential: smooth
        if (speedKmh > 3) return 1300;    // Slow/parking
        return 1500;                       // Stationary/walking: very smooth
    }, [duration]);

    // ── Animation engine ──
    const lastIconUpdateRef = useRef(0); // Throttle icon updates for performance

    const animateToPosition = useCallback((newPos, newHeading, currentSpeed) => {
        if (!markerRef.current) return;

        // First position: just place, no animation
        if (!currentPosRef.current) {
            currentPosRef.current = { ...newPos };
            currentHeadingRef.current = newHeading;
            markerRef.current.setPosition(new window.google.maps.LatLng(newPos.lat, newPos.lng));
            
            // Update rotation for both icon types
            if (iconGenerator) {
                markerRef.current.setIcon(iconGenerator(newHeading));
            } else {
                const curIcon = markerRef.current.getIcon();
                if (curIcon && typeof curIcon === 'object') {
                    if ('rotation' in curIcon) {
                        markerRef.current.setIcon({ ...curIcon, rotation: newHeading });
                    }
                }
            }
            return;
        }

        const startPos = { ...currentPosRef.current };
        const endPos = { ...newPos };
        const startHeading = currentHeadingRef.current;
        const endHeading = newHeading || 0;
        const startTime = performance.now();
        const animDuration = getAdaptiveDuration(currentSpeed);

        if (animRef.current) cancelAnimationFrame(animRef.current);

        const animate = (currentTime) => {
            if (!markerRef.current) return;

            const elapsed = currentTime - startTime;
            const progress = Math.min(elapsed / animDuration, 1);

            // Cubic ease-in-out: smooth start + smooth stop
            const eased = progress < 0.5
                ? 4 * progress * progress * progress
                : 1 - Math.pow(-2 * progress + 2, 3) / 2;

            const lat = startPos.lat + (endPos.lat - startPos.lat) * eased;
            const lng = startPos.lng + (endPos.lng - startPos.lng) * eased;

            // Heading interpolation with 360° shortest-path wraparound
            let headingDiff = endHeading - startHeading;
            if (headingDiff > 180) headingDiff -= 360;
            if (headingDiff < -180) headingDiff += 360;
            const currentH = ((startHeading + headingDiff * eased) + 360) % 360;

            // Position updates at 60fps (cheap — just lat/lng)
            markerRef.current.setPosition(new window.google.maps.LatLng(lat, lng));
            currentPosRef.current = { lat, lng };
            currentHeadingRef.current = currentH;

            // Icon updates throttled to ~15fps (expensive — full SVG data URI rebuild)
            // This is the key to smooth rotation without stutter
            const shouldUpdateIcon = (currentTime - lastIconUpdateRef.current) > 66 || progress >= 1;
            
            if (shouldUpdateIcon) {
                lastIconUpdateRef.current = currentTime;
                if (iconGenerator) {
                    markerRef.current.setIcon(iconGenerator(currentH));
                } else {
                    const curIcon = markerRef.current.getIcon();
                    if (curIcon && typeof curIcon === 'object' && 'rotation' in curIcon) {
                        markerRef.current.setIcon({ ...curIcon, rotation: currentH });
                    }
                }
            }

            if (progress < 1) {
                animRef.current = requestAnimationFrame(animate);
            } else {
                currentPosRef.current = { ...endPos };
                currentHeadingRef.current = endHeading;
                targetPosRef.current = { ...endPos };
            }
        };

        animRef.current = requestAnimationFrame(animate);
    }, [getAdaptiveDuration, iconGenerator]);

    // Trigger animation when position/heading changes
    useEffect(() => {
        if (!position) return;

        // Dead-zone: skip if barely moved and heading barely changed (~0.8m threshold)
        if (targetPosRef.current) {
            const dLat = Math.abs(position.lat - targetPosRef.current.lat);
            const dLng = Math.abs(position.lng - targetPosRef.current.lng);
            const hDelta = Math.abs((heading || 0) - currentHeadingRef.current);
            const hWrapped = Math.min(hDelta, 360 - hDelta);

            // ~0.8m position threshold AND < 3° heading threshold → skip
            if (dLat < 0.000008 && dLng < 0.000008 && hWrapped < 3) {
                return;
            }
        }

        targetPosRef.current = { ...position };
        animateToPosition(position, heading || 0, speed);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [position?.lat, position?.lng, heading, animateToPosition, speed]);

    return null;
};

export default AnimatedMarker;
